import { ConfigurationState, FormState } from '../../types/FormState.types'
import { ConfigurationChanges, JsonValue, ShadowDocument, ShadowState } from '../../types/ShadowDocument.types'
import { Shadow } from '../../types/Shadow.types'
import { createEmptyDeviceThing } from './deviceThingDefaults'

const defaults = createEmptyDeviceThing()
export const shadowNames = Object.values(Shadow)
export type FieldErrors = Record<string, string>
type Value = string | number | boolean

// Text forms of a boolean found on devices, as [true, false].
const BOOLEAN_TEXTS = [
    ['true', 'false'],
    ['yes', 'no'],
    ['1', '0'],
]
const booleanTexts = (text: string) => BOOLEAN_TEXTS.find((pair) => pair.includes(text.trim().toLowerCase()))

function isConfigurationField(shadow: Shadow, key: string): boolean {
    return Object.prototype.hasOwnProperty.call(defaults[shadow], key)
}

// Decodes a device or form value into the editor's type; a value of the wrong type throws a message for the user.
export function normalizeValue(shadow: Shadow, key: string, value: unknown): Value {
    if (!isConfigurationField(shadow, key)) throw new Error('Nieznany parametr konfiguracji.')
    if (key === 'UI_SHOW_LAST_DISTRIBUTIONS_OF_PRODUCT' && value === 'ONLY4RENTABLE') return value

    const template = (defaults[shadow] as unknown as ShadowState)[key]
    if (typeof template === 'boolean') {
        if (typeof value === 'boolean') return value
        if (value === 0 || value === 1) return value === 1
        if (typeof value === 'string') {
            const texts = booleanTexts(value)
            if (texts) return texts[0] === value.trim().toLowerCase()
        }
        throw new Error('Wybierz tak lub nie.')
    }
    if (typeof template === 'number') {
        if ((typeof value !== 'number' && typeof value !== 'string') || String(value).trim() === '') {
            throw new Error('Wprowadź liczbę całkowitą.')
        }
        const number = Number(value)
        if (!Number.isSafeInteger(number)) throw new Error('Wprowadź liczbę całkowitą.')
        return number
    }
    // Devices may store text settings such as a PIN or screen width as JSON numbers.
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
    if (typeof value !== 'string') throw new Error('Wprowadź tekst.')
    return key === 'RESTAPI_PROTOCOL' ? value.toLowerCase() : value
}

// Writes a decoded value in the representation the device already uses, so the firmware never sees its JSON type change.
export function encodeValue(value: Value, source: JsonValue | undefined): Value {
    if (typeof value === 'boolean') {
        if (typeof source === 'number') return value ? 1 : 0
        const texts = typeof source === 'string' ? booleanTexts(source) : undefined
        if (typeof source !== 'string' || !texts) return value
        const text = texts[value ? 0 : 1]
        return source === source.toUpperCase() ? text.toUpperCase() : text
    }
    if (typeof value === 'number' && typeof source === 'string') return String(value)
    // Leading zeros would be lost in a number, so such text stays text.
    if (typeof value === 'string' && typeof source === 'number' && /^(0|[1-9]\d*)$/.test(value)) return Number(value)
    return value
}

function comparable(shadow: Shadow, key: string, value: unknown): unknown {
    try {
        return normalizeValue(shadow, key, value)
    } catch {
        return value
    }
}

export function sameConfigurationValue(shadow: Shadow, key: string, a: unknown, b: unknown): boolean {
    return comparable(shadow, key, a) === comparable(shadow, key, b)
}

export function validateChanges(
    initial: FormState,
    draft: FormState
): {
    changes: ConfigurationChanges
    errors: FieldErrors
    dirty: boolean
} {
    const changes: ConfigurationChanges = {}
    const errors: FieldErrors = {}
    let dirty = false
    for (const shadow of shadowNames) {
        const before = initial[shadow] as unknown as Record<string, unknown>
        const after = draft[shadow] as unknown as Record<string, unknown>
        for (const key of Object.keys(defaults[shadow])) {
            const path = `${shadow}.${key}`
            if (sameConfigurationValue(shadow, key, before[key], after[key])) continue
            dirty = true
            try {
                changes[shadow] = { ...changes[shadow], [key]: normalizeValue(shadow, key, after[key]) }
            } catch (error) {
                errors[path] = (error as Error).message
            }
        }
    }
    return { changes, errors, dirty }
}

// Desired wins over reported: it is what the device has been asked to apply.
export function effectiveState(document: ShadowDocument): ShadowState {
    return { ...document.state.reported, ...document.state.desired }
}

// Decode each field independently: a firmware-specific value must not hide valid settings.
// Defaults are for rendering only. Unreadable fields are not editable and are never sent in a patch.
export function mergeConfiguration<S extends Shadow>(
    shadow: S,
    state: ShadowState
): {
    configuration: ConfigurationState[S]
    fieldReadErrors: FieldErrors
} {
    const result: Record<string, unknown> = { ...defaults[shadow] }
    const fieldReadErrors: FieldErrors = {}
    for (const key of Object.keys(result)) {
        if (state[key] == null) continue
        try {
            result[key] = normalizeValue(shadow, key, state[key])
        } catch {
            fieldReadErrors[`${shadow}.${key}`] = 'Format tego parametru nie jest obsługiwany przez edytor. Wartość pozostanie bez zmian.'
        }
    }
    return { configuration: result as unknown as ConfigurationState[S], fieldReadErrors }
}
